import React, { useEffect, useMemo } from "react";
import { Col, Input, Row, Segmented, Select, Tag, Typography } from "antd";
import {
    CURRENT_DATE_VARIABLE,
    IUDMOperand,
    METRIC_ONLY_SCOPE,
    METRIC_ONLY_STAT,
    SCOPE_OPTIONS,
    STAT_OPTIONS,
    VARIABLE_LABEL_OVERRIDES,
} from "./constants";
import { VariableOption } from "./types";
import { splitMetricIdentifier } from "./utils";

const { Text } = Typography;

interface OperandBlockProps {
    operandName: string;
    params: IUDMOperand;
    metricOptions: any[];
    variableOptions: VariableOption[];
    onChange: (params: IUDMOperand) => void;
    // The operand is only the first argument of value_to_hit_acwr: show the metric alone.
    metricOnly?: boolean;
}

const OperandBlock: React.FC<OperandBlockProps> = ({
    operandName,
    params,
    metricOptions,
    variableOptions,
    onChange,
    metricOnly = false,
}) => {
    const { base: metricBase, stat } = splitMetricIdentifier(params.metric_identifier);
    // metric_baseline is GPS-only on the backend (native gps-N or UDM gps-cN); hide it for
    // other metric types so an invalid config can't be built.
    const isGpsMetric = metricBase.startsWith("gps-");
    // Every variable other than the current date value is read out of MiscStats, which the
    // backend only computes for the GPS and testing families, so wellness resolves for
    // "latest" alone (validate_wellness_variable_pairing).
    const isWellnessMetric = metricBase.startsWith("wellness-");

    // Build variable select options: apply label overrides and group rolling values
    const variableSelectOptions = useMemo(() => {
        const flat: { label: string; value: string }[] = [];
        const rolling: { label: string; value: string }[] = [];

        variableOptions.forEach((opt) => {
            if (opt.value === "metric_baseline" && !isGpsMetric) return;
            if (isWellnessMetric && opt.value !== CURRENT_DATE_VARIABLE) return;
            const displayLabel = VARIABLE_LABEL_OVERRIDES[opt.value.toLowerCase()] ?? opt.label;
            const entry = { label: displayLabel, value: opt.value };
            if (opt.value.toLowerCase().startsWith("rolling")) {
                rolling.push(entry);
            } else {
                flat.push(entry);
            }
        });

        if (rolling.length === 0) return flat;

        return [...flat, { label: "Rolling Window", options: rolling } as any];
    }, [variableOptions, isGpsMetric, isWellnessMetric]);

    // A config saved before wellness was restricted can still hold a variable wellness
    // cannot resolve. Normalise it on load so the editor shows what the backend accepts.
    useEffect(() => {
        if (isWellnessMetric && params.variable !== CURRENT_DATE_VARIABLE) {
            onChange({ ...params, variable: CURRENT_DATE_VARIABLE });
        }
    }, [isWellnessMetric, params.variable]);

    // A metric-only operand keeps fixed values in the fields the backend does not read, so a
    // saved config never holds a setting that looks like it matters.
    useEffect(() => {
        if (!metricOnly) return;
        const statIsFixed = !metricBase || stat === METRIC_ONLY_STAT;
        if (params.scope === METRIC_ONLY_SCOPE && params.variable === CURRENT_DATE_VARIABLE && statIsFixed) return;
        onChange({
            ...params,
            scope: METRIC_ONLY_SCOPE,
            variable: CURRENT_DATE_VARIABLE,
            metric_identifier: metricBase ? `${metricBase}-${METRIC_ONLY_STAT}` : params.metric_identifier,
        });
    }, [metricOnly, params.scope, params.variable, metricBase, stat]);

    const metricIsAllowed =
        !metricBase || metricOptions.some((group) => group.options?.some((o: any) => o.value === metricBase));

    const handleMetricChange = (value: string | undefined) => {
        if (metricOnly) {
            onChange({
                ...params,
                scope: METRIC_ONLY_SCOPE,
                variable: CURRENT_DATE_VARIABLE,
                metric_identifier: value ? `${value}-${METRIC_ONLY_STAT}` : "",
            });
            return;
        }
        // Preserve the current stat; default to "avg" when a fresh metric is chosen.
        // Clear a stale metric_baseline selection if the new metric isn't GPS, and drop a
        // variable wellness cannot resolve back to the current date value.
        // `value` is undefined when the select's clear button is used.
        const nextIsGps = !!value?.startsWith("gps-");
        const nextIsWellness = !!value?.startsWith("wellness-");
        let nextVariable =
            params.variable === "metric_baseline" && !nextIsGps ? "" : params.variable;
        if (nextIsWellness && nextVariable !== CURRENT_DATE_VARIABLE) {
            nextVariable = CURRENT_DATE_VARIABLE;
        }
        onChange({
            ...params,
            variable: nextVariable,
            metric_identifier: value ? `${value}-${stat || "avg"}` : "",
        });
    };

    const handleStatChange = (value: string | number) => {
        if (!metricBase) return;
        onChange({ ...params, metric_identifier: `${metricBase}-${String(value)}` });
    };

    return (
        <div className="udm-operand-block">
            <div className="udm-operand-header">
                <Tag color="blue" className="udm-operand-tag">{params.label || "Metric"}</Tag>
                {params.label && (
                    <Text type="secondary" className="udm-operand-internal-name">({operandName})</Text>
                )}
            </div>
            <Row gutter={[8, 8]} align="middle">
                <Col span={24}>
                    <div className="udm-field-row">
                        <Text className="udm-field-label">Metric</Text>
                        <Select
                            size="small"
                            style={{ flex: 1 }}
                            options={metricOptions}
                            value={metricBase || undefined}
                            onChange={handleMetricChange}
                            placeholder="Select metric"
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                        {params.variable === "latest" && !metricOnly && (
                            <Segmented
                                size="small"
                                options={STAT_OPTIONS}
                                value={stat || "avg"}
                                onChange={handleStatChange}
                                disabled={!metricBase}
                                className="udm-stat-segmented"
                            />
                        )}
                    </div>
                </Col>
                {metricOnly ? (
                    <Col span={24}>
                        <Text type={metricIsAllowed ? "secondary" : "danger"} className="udm-section-hint">
                            {metricIsAllowed
                                ? "value_to_hit_acwr uses only the metric: each player's daily total, with the team's ACWR settings."
                                : "Pick a GPS metric that has an ACWR."}
                        </Text>
                    </Col>
                ) : (
                    <>
                        <Col span={12}>
                            <div className="udm-field-row">
                                <Text className="udm-field-label">Scope</Text>
                                <Select
                                    size="small"
                                    style={{ flex: 1 }}
                                    options={SCOPE_OPTIONS}
                                    value={params.scope || undefined}
                                    onChange={(v) => onChange({ ...params, scope: v })}
                                    placeholder="Select scope"
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="udm-field-row">
                                <Text className="udm-field-label">Variable</Text>
                                {variableOptions.length > 0 ? (
                                    <Select
                                        size="small"
                                        style={{ flex: 1 }}
                                        options={variableSelectOptions}
                                        value={params.variable || undefined}
                                        onChange={(v) => onChange({ ...params, variable: v })}
                                        placeholder="Select variable"
                                    />
                                ) : (
                                    <Input
                                        size="small"
                                        style={{ flex: 1 }}
                                        value={params.variable}
                                        onChange={(e) => onChange({ ...params, variable: e.target.value })}
                                        placeholder="e.g. latest"
                                    />
                                )}
                            </div>
                        </Col>
                    </>
                )}
            </Row>
        </div>
    );
};

export default OperandBlock;
